import { InscriptionTurn } from '@/Domain/enum';
import { ITurnWithReferences } from '@/Domain/interfaces/Process/ITurnWithReference';
import MultiLanguage from '../MultiLanguage';
import { IProcessReference } from '@/Domain/interfaces/Process/IProcessReference';
import Vacancies from '../ProcessStructure/Vacancies';
import VacanciesAttached from '../ProcessStructure/VacanciesAttached';

export default class TurnWithReferences {

    get turnActive() {
        return this.turns.find(turn => turn.active);
    }
    turns: ITurnWithReferences[] = [
        { type: InscriptionTurn.enum.FREE, active: true, isExtraVacanciesActive: false, title: 'lang.shared.free', total: 0, references: [], vacancies: 0, extraVacancies: 0 },
        { type: InscriptionTurn.enum.FREE_DISABILITY, active: false, isExtraVacanciesActive: false, title: 'lang.shared.freeDisability', total: 0, references: [], vacancies: 0, extraVacancies: 0 },
        { type: InscriptionTurn.enum.INTERNAL, active: false, isExtraVacanciesActive: false, title: 'lang.shared.internal', total: 0, references: [], vacancies: 0, extraVacancies: 0 },
        { type: InscriptionTurn.enum.INTERNAL_DISABILITY, active: false, isExtraVacanciesActive: false, title: 'lang.shared.internalDisability', total: 0, references: [], vacancies: 0, extraVacancies: 0 },
        { type: InscriptionTurn.enum.MOBILITY, active: false, isExtraVacanciesActive: false, title: 'lang.shared.mobility', total: 0, references: [], vacancies: 0, extraVacancies: 0 }
    ];
    bopNumber: string = '';

    constructor(data: { bopNumber: string } = { bopNumber: '' }) {
        this.bopNumber = data.bopNumber;
    }

    getReferences(): IProcessReference[] {
        return this.turns.reduce((acc: IProcessReference[], turn) => {
            return acc.concat(turn.references);
        }, []);
    }

    getVacancies(): Vacancies {
        const turnVacancies = this.turns.flatMap(turn => {
            return { turn: turn.type, vacancies: turn.vacancies };
        }).sort((a, b) => a.turn - b.turn);

        return new Vacancies({
            free: turnVacancies[InscriptionTurn.enum.FREE].vacancies,
            internal: turnVacancies[InscriptionTurn.enum.INTERNAL].vacancies,
            internalDisability: turnVacancies[InscriptionTurn.enum.INTERNAL_DISABILITY].vacancies,
            freeDisability: turnVacancies[InscriptionTurn.enum.FREE_DISABILITY].vacancies,
            mobility: turnVacancies[InscriptionTurn.enum.MOBILITY].vacancies,
            total: this.getTotal()
        });
    }

    updateCount() {
        this.turns.forEach(turn => {
            const vacanciesSum = turn.references
                .reduce((acc, reference) => acc + (reference.totalVacancies || 0) + (reference.totalExtraVacancies || 0), 0);
            turn.total = (turn.vacancies || 0) + (turn.extraVacancies || 0) + vacanciesSum;
        });
    }

    updateTurnsIsExtraVacanciesActive() {
        this.turns.forEach(turn => {
            if (turn.isExtraVacanciesActive) {
                return;
            }
            turn.isExtraVacanciesActive = turn.references.some(ref => ref.totalExtraVacancies > 0) || turn.extraVacancies > 0;
        });
    }

    changeActiveTurn(turnType: number) {
        this.turns.forEach(turn => {
            turn.active = turn.type === turnType;
        });
    }

    changeIsExtraVacanciesActive() {
        if (this.turnActive) {
            this.turnActive.isExtraVacanciesActive = !this.turnActive.isExtraVacanciesActive;
        }
    }

    buildTurns(data: IProcessReference[] | Vacancies) {
        if (data instanceof Vacancies) {
            this.buildTurnsFromVacancies(data);
        } else {
            this.buildTurnsFromReferences(data);
        }
        this.updateCount();
    }

    addExtraVacancies(extraVacancies: VacanciesAttached) {
        this.turns.forEach(turn => {
            turn.extraVacancies = extraVacancies.getVacancies(turn.type);
        });
        this.updateCount();
        this.updateTurnsIsExtraVacanciesActive();
    }

    anyTurnHasReferences() {
        return this.turns.some(turn => turn.references.length > 0);
    }

    anyTurnHasVacancies() {
        return this.turns.some(turn => turn.vacancies > 0);
    }

    areTurnsValid(languages: string[] = []): boolean {
        return this.turns.every(turn => {
            return this.isTurnValid(turn, languages);
        });
    }

    isTurnValid(turn: ITurnWithReferences, languages: string[]): boolean {
        return turn.references.length > 0
            ? this.areReferencesValid(turn.references, languages) && this.areReferencesExtraVacanciesValid(turn)
            : this.areVacanciesValid(turn.vacancies) && this.areExtraVacanciesValid(turn);
    }

    getTurnsWithErrors(languages: string[]) {
        return this.turns.map(turn => {
            if (!this.isTurnValid(turn, languages)) {
                return turn;
            }
            return undefined;
        }).filter(turn => turn !== undefined);
    }

    getReferenceById(referenceId: string): IProcessReference | undefined {
        return this.getReferences().find(ref => ref.id === referenceId);
    }

    turnHasExtraVacancies(turn: ITurnWithReferences) {
        return turn.isExtraVacanciesActive && turn.references.some(ref => ref.totalExtraVacancies > 0);
    }

    areExtraVacanciesValid(turn: ITurnWithReferences): boolean {
        return turn.isExtraVacanciesActive ? turn.extraVacancies > 0 : turn.extraVacancies === 0;
    }
    
    private areReferencesValid(references: IProcessReference[], languages: string[]): boolean {
        return references.every(ref => {
            const baseValidation =
                ref.description !== undefined &&
                ref.reference !== undefined &&
                ref.totalVacancies > 0;

            if (languages.length > 0) {
                return baseValidation && ref.description.allHasValue(languages);
            }
            return baseValidation;
        });
    }

    private areReferencesExtraVacanciesValid(turn: ITurnWithReferences): boolean {
        if (turn.isExtraVacanciesActive) {
            return turn.references.some(ref => ref.totalExtraVacancies > 0);
        }
        return turn.references.every(ref => ref.totalExtraVacancies === 0);
    }

    private areVacanciesValid(vacancies: number): boolean {
        return vacancies >= 0;
    }

    private getTotal() {
        return this.turns.reduce((acc, turn) => acc + turn.total, 0);
    }

    private buildTurnsFromReferences(references: IProcessReference[]) {
        this.turns = this.turns.map(turn => {
            const referencesByTurn = references.filter(ref => ref.turn === turn.type);

            return {
                ...turn,
                references: referencesByTurn.map(ref => ({ ...ref, description: new MultiLanguage(ref.description), totalExtraVacancies: ref.totalExtraVacancies || 0 }))
            };
        });
        this.updateTurnsIsExtraVacanciesActive();
    }

    private buildTurnsFromVacancies(vacancies: Vacancies) {
        this.turns = [
            { type: InscriptionTurn.enum.FREE, active: true, isExtraVacanciesActive: false, title: 'lang.shared.free', total: 0, references: [], vacancies: vacancies.freeVacancies, extraVacancies: 0 },
            { type: InscriptionTurn.enum.FREE_DISABILITY, active: false, isExtraVacanciesActive: false, title: 'lang.shared.freeDisability', total: 0, references: [], vacancies: vacancies.freeDisabilityVacancies, extraVacancies: 0 },
            { type: InscriptionTurn.enum.INTERNAL, active: false, isExtraVacanciesActive: false, title: 'lang.shared.internal', total: 0, references: [], vacancies: vacancies.internalVacancies, extraVacancies: 0 },
            { type: InscriptionTurn.enum.INTERNAL_DISABILITY, active: false, isExtraVacanciesActive: false, title: 'lang.shared.internalDisability', total: 0, references: [], vacancies: vacancies.internalDisabilityVacancies, extraVacancies: 0 },
            { type: InscriptionTurn.enum.MOBILITY, active: false, isExtraVacanciesActive: false, title: 'lang.shared.mobility', total: 0, references: [], vacancies: vacancies.mobilityVacancies, extraVacancies: 0 }
        ];
    }
}
